import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, ArrowRight, BookOpen, Loader2 } from "lucide-react";
import Reveal from "../components/Reveal";
import SaveButton from "../components/SaveButton";

/**
 * LearnWorld — deep view of a single world of computing.
 * Loads the world's chapter list from /api/curriculum/worlds/:slug and
 * renders it as a paginated pop-up-book contents page.
 */
export default function LearnWorld() {
  const { world: worldSlug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!worldSlug) return;
    let cancelled = false;
    setData(null);
    api.get(`/curriculum/worlds/${worldSlug}?limit=200`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ world: null, articles: [], total: 0 }); });
    return () => { cancelled = true; };
  }, [worldSlug]);

  if (!data) return <div className="container-narrow py-24 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  if (!data.world) {
    return (
      <div className="container-narrow py-24 text-center" data-testid="learn-world-404">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-4">This world hasn't been mapped yet.</h1>
        <Link to="/learn" className="btn-outline-ink text-sm inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Back to the twelve worlds</Link>
      </div>
    );
  }
  const w = data.world;

  return (
    <div className="container-narrow py-12 md:py-16" data-testid={`learn-world-${w.slug}`}>
      <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink2 hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" /> The twelve worlds
      </Link>

      <Reveal className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-2">{w.kicker}</div>
        <h1 className="font-heading text-5xl md:text-6xl font-semibold text-ink leading-[1.05] mb-3">
          {w.title}
        </h1>
        <p className="text-lg text-ink2 italic leading-relaxed max-w-2xl">{w.one_line}</p>
        <div className="mt-3 text-sm text-ink2 font-mono">
          {data.total.toLocaleString()} {data.total === 1 ? "chapter" : "chapters"} in this world
        </div>
      </Reveal>

      {data.articles.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-ink/30 p-10 text-center text-ink2" data-testid="learn-world-empty">
          <p className="italic mb-3">This world hasn't been filled in yet.</p>
          <p className="text-sm">Ms. Pradhan is still writing — check back after the next chapter import.</p>
        </div>
      ) : (
        <div className="rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] overflow-hidden">
          <ol className="divide-y divide-ink/10">
            {data.articles.map((a, i) => (
              <li key={a.slug}>
                <div className="flex items-start px-5 md:px-7 py-4 hover:bg-ink/[0.04] transition-colors group">
                  <Link
                    to={`/library/${a.slug}`}
                    className="flex-1 flex items-start gap-4 min-w-0"
                    data-testid={`learn-world-article-${a.code || a.slug}`}
                  >
                    <div className="text-[10px] font-mono font-bold text-ink/50 pt-1 whitespace-nowrap min-w-[36px]">
                      {String(i + 1).padStart(3, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-base md:text-lg font-semibold text-ink group-hover:text-marigold transition-colors leading-snug">
                        {a.title}
                      </div>
                      {a.summary && (
                        <div className="text-xs md:text-sm text-ink2 mt-1 line-clamp-2 leading-relaxed">
                          {a.summary}
                        </div>
                      )}
                      <div className="text-[10px] font-mono text-ink/40 mt-1.5">
                        {a.module_code}·A{String(a.article_num || 0).padStart(2, "0")}
                        {a.word_count ? ` · ${Math.max(1, Math.round(a.word_count / 200))} min` : ""}
                      </div>
                    </div>
                  </Link>
                  <div className="pt-1 pl-3 flex items-center gap-2 flex-shrink-0">
                    <SaveButton slug={a.slug} title={a.title} size="sm" testId={`learn-world-save-${a.code || a.slug}`} />
                    <ArrowRight className="w-4 h-4 text-ink/40 group-hover:text-marigold group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
